import { useState } from "react";
import { useRoute, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { agentSharingService } from "@/services/agentSharingService";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  ArrowRight,
  Share2,
  Lock,
  Bot,
  FolderOpen,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { buildLoginUrlWithReturnTo } from "@/lib/publicRoutes";

export default function AgentFolderShareAcceptance() {
  const [, params] = useRoute("/agents/folders/share/:token");
  const [location, setLocation] = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const token = params?.token || "";
  const [hasResponded, setHasResponded] = useState(false);

  const { data: validation, isLoading: isValidating } = useQuery({
    queryKey: ["/api/agents/folders/share/validate", token],
    queryFn: async () => {
      if (!token) return null;
      return await agentSharingService.validateFolderShareLink(token);
    },
    enabled: !!token,
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => agentSharingService.acceptFolderShareInvitation(token),
    onSuccess: (access) => {
      setHasResponded(true);
      toast({
        title: "Access granted",
        description: "You now have access to all agents in this folder",
      });
      const folderParam = access.copiedFolderId ? `?folder=${access.copiedFolderId}` : "";
      setTimeout(() => {
        setLocation(`/ai-agents${folderParam}`);
      }, 1500);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to accept",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async () => agentSharingService.rejectFolderShareInvitation(token),
    onSuccess: () => {
      setHasResponded(true);
      toast({ title: "Invitation declined" });
      setTimeout(() => setLocation("/ai-agents"), 1500);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to reject",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });

  if (authLoading || isValidating) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Sign in required
            </CardTitle>
            <CardDescription>Sign in to accept this shared agent folder</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setLocation(buildLoginUrlWithReturnTo(location))} className="w-full">
              Go to Sign In
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!validation?.isValid) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-destructive flex items-center gap-2">
              <XCircle className="h-5 w-5" />
              Invalid Link
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription>{validation?.message || "This link is invalid or expired."}</AlertDescription>
            </Alert>
            <Button variant="outline" onClick={() => setLocation("/ai-agents")} className="w-full">
              Go to AI Agents
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { share, folder, agents, access } = validation;

  if (access?.status === "accepted" || hasResponded) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-green-600 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              Access Granted
            </CardTitle>
            <CardDescription>You have accepted the shared agent folder</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => {
                const folderParam = access?.copiedFolderId ? `?folder=${access.copiedFolderId}` : "";
                setLocation(`/ai-agents${folderParam}`);
              }}
              className="w-full"
            >
              Go to AI Agents
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (access?.status === "rejected") {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Access Declined</CardTitle>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => setLocation("/ai-agents")} className="w-full">
              Go to AI Agents
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Agent Folder Invitation
          </CardTitle>
          <CardDescription>You&apos;ve been invited to access a folder of shared AI agents</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {folder && (
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/50">
              <FolderOpen className="h-5 w-5 text-purple-500 shrink-0" />
              <div>
                <h3 className="font-semibold text-sm">{folder.name}</h3>
                <p className="text-xs text-muted-foreground">{folder.agentCount} agent(s)</p>
              </div>
            </div>
          )}

          {agents.length > 0 && (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {agents.map((agent) => (
                <div key={agent.id} className="flex items-center gap-2 p-2 rounded border text-sm">
                  <Bot className="h-4 w-4 text-purple-500 shrink-0" />
                  <span className="truncate font-medium">{agent.name}</span>
                </div>
              ))}
            </div>
          )}

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Accept all agents?</AlertTitle>
            <AlertDescription>
              Accepting will copy all agents in this folder into your library so you can use them on threads.
            </AlertDescription>
          </Alert>

          {share?.expiresAt && (
            <p className="text-xs text-muted-foreground text-center">
              Link expires {formatDistanceToNow(new Date(share.expiresAt), { addSuffix: true })}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => rejectMutation.mutate()}
              disabled={acceptMutation.isPending || rejectMutation.isPending}
              className="flex-1"
            >
              Decline
            </Button>
            <Button
              onClick={() => acceptMutation.mutate()}
              disabled={acceptMutation.isPending || rejectMutation.isPending}
              className="flex-1"
            >
              {acceptMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Accepting...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Accept All
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
